import { db } from './index';

export const queueRepo = {
  async enqueue(entry) {
    return db.mutationQueue.add({
      ...entry,
      status: 'pending',
      attempts: 0,
      createdAt: new Date().toISOString(),
    });
  },

  async listPending(limit = 50) {
    // Dexie's .limit() truncates in index (status) order, so calling it before
    // .sortBy would drop items regardless of createdAt — a `create` could be
    // excluded from the same batch as its dependent `checkin`, causing avoidable
    // "waiting for create" failures. Sort by createdAt FIRST, then slice.
    const rows = await db.mutationQueue
      .where('status')
      .anyOf('pending', 'failed')
      .sortBy('createdAt');
    return rows.slice(0, limit);
  },

  async markInFlight(id) {
    await db.mutationQueue.update(id, {
      status: 'in_flight',
      lastAttemptAt: new Date().toISOString(),
    });
  },

  // Recover mutations left in 'in_flight' by an app kill/crash mid-drain (very
  // common on iOS). Without this they are excluded from listPending forever and
  // never retried, silently losing the write. Called at the start of every drain.
  // Safe because all executed operations are idempotent (habit create dedupes on
  // clientId server-side; checkin/delete are idempotent by key).
  async requeueStaleInFlight() {
    const stale = await db.mutationQueue.where('status').equals('in_flight').toArray();
    for (const m of stale) {
      if (m.id === undefined) continue;
      await db.mutationQueue.update(m.id, { status: 'pending' });
    }
    return stale.length;
  },

  async markSucceeded(id) {
    await db.mutationQueue.delete(id);
  },

  async resetToPending(id) {
    await db.mutationQueue.update(id, { status: 'pending' });
  },

  async markFailed(id, error, dead = false) {
    const row = await db.mutationQueue.get(id);
    if (!row) return;
    await db.mutationQueue.update(id, {
      status: dead ? 'dead' : 'failed',
      attempts: row.attempts + 1,
      lastError: error,
    });
  },

  async rewriteRecordId(entity, fromId, toId) {
    const matches = await db.mutationQueue
      .where('entity')
      .equals(entity)
      .filter((m) => m.recordId === fromId)
      .toArray();
    for (const m of matches) {
      if (m.id === undefined) continue;
      await db.mutationQueue.update(m.id, { recordId: toId });
    }
  },

  async rewriteCheckinRecordId(fromHabitId, toHabitId) {
    // Checkin recordIds are `${habitId}#${date}` — rewrite the habit half when the
    // habit gets rekeyed after its create lands on the server.
    const matches = await db.mutationQueue
      .where('entity')
      .equals('checkin')
      .filter((m) => typeof m.recordId === 'string' && m.recordId.startsWith(`${fromHabitId}#`))
      .toArray();
    for (const m of matches) {
      if (m.id === undefined) continue;
      const newRecordId = m.recordId.replace(`${fromHabitId}#`, `${toHabitId}#`);
      const payload =
        m.payload && typeof m.payload === 'object'
          ? { ...m.payload, habitId: toHabitId }
          : m.payload;
      await db.mutationQueue.update(m.id, { recordId: newRecordId, payload });
    }
  },

  async pendingCount() {
    return db.mutationQueue.where('status').anyOf('pending', 'failed', 'in_flight').count();
  },

  async clearForUser() {
    // Mutation queue is global (not user-scoped). On sign-out we leave pending
    // anon mutations alone — they belong to the anon user and will drain on
    // next sign-in via the migration path.
  },
};
